import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Sparkles, Bug, Calendar, Loader2 } from 'lucide-react';
import api from '../services/api';

const ChangelogPage = () => {
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChangelog();
  }, []);

  const fetchChangelog = async () => {
    try {
      const response = await api.get('/changelog');
      setVersions(response.data);
    } catch (err) {
      console.error('Failed to fetch changelog:', err);
      setError('Failed to load changelog');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-slate-900">Changelog</h1>
              <p className="text-sm text-slate-500">Track updates and improvements to PTC Builder</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {versions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-500">No changelog entries available.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {versions.map((release, index) => (
              <Card key={release.version} className={index === 0 ? 'border-indigo-200 bg-indigo-50/30' : ''}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-outfit text-xl flex items-center gap-3">
                      Version {release.version}
                      {index === 0 && (
                        <Badge className="bg-indigo-600 text-white">Latest</Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Calendar className="h-4 w-4" />
                      {release.date}
                    </div>
                  </div>
                  <CardDescription>
                    {release.changes.filter(c => c.type === 'enhancement').length} enhancements, {release.changes.filter(c => c.type === 'bug_fix').length} bug fixes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {release.changes.map((change, changeIndex) => (
                      <div 
                        key={change.id || changeIndex} 
                        className="flex gap-4 p-3 rounded-lg bg-white border border-slate-100"
                        data-testid={`changelog-item-${release.version}-${changeIndex}`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {change.type === 'enhancement' ? (
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                              <Sparkles className="h-4 w-4 text-emerald-600" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                              <Bug className="h-4 w-4 text-amber-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-900">{change.title}</span>
                            <Badge 
                              variant="outline" 
                              className={change.type === 'enhancement' 
                                ? 'text-emerald-700 border-emerald-200 bg-emerald-50' 
                                : 'text-amber-700 border-amber-200 bg-amber-50'
                              }
                            >
                              {change.type === 'enhancement' ? 'Enhancement' : 'Bug Fix'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{change.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-slate-500">
          <p>PTC Builder by EDUx</p>
          <p className="mt-1">For feature requests or bug reports, please contact your administrator.</p>
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
